/**
 * Keeps the room token of an open call alive. Without it, a short network drop
 * after minute 15 ends the call with the message Token is expired. Robert's
 * group call ended that way on 2026-10-05.
 *
 * A room token lives 15 minutes (vrs-server lib/room-token-grant.ts). Prosody
 * checks it when the connection signs in, and again when a dropped connection
 * resumes: lib-jitsi-meet resumes the stream (XEP-0198) on its service URL,
 * which still carries the token from the join, and mod_auth_token.lua refuses
 * an expired one. lib-jitsi-meet then reports PASSWORD_REQUIRED, the room
 * shows "Token is expired" and the call is gone. A Wi-Fi to cell change or a
 * short trip out of the app is enough.
 *
 * While the connection is open, the refresher gets a new token for the same
 * room before the old one expires and writes it into the connection's service
 * URL, so the next resume signs in with a live token. A full reload already
 * gets a new token (stripExpiredRoomTokenFromURL, addJitsiRoomToken).
 */
/** Get the new token this long before the old one expires. */
export const REFRESH_LEAD_MS = 3 * 60000;
/** Wait at least this long, also when the token is already close to expiry. */
export const MIN_REFRESH_DELAY_MS = 5000;
/** After a failed attempt (network, server), try again after this long. */
export const RETRY_DELAY_MS = 30000;
/** HTTP answers that say a new token will not come: signed out, out of the room, call moved. */
const FINAL_STATUSES = [401, 403, 409];
/**
 * The exp and room claims of a room token, read without a signature check
 * (the client cannot check it; Prosody does).
 *
 * @param {string} token - The room JWT.
 * @returns {Object|undefined}
 */
export function roomTokenClaims(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (!payload || typeof payload !== 'object') {
            return undefined;
        }
        return {
            exp: typeof payload.exp === 'number' && Number.isFinite(payload.exp) ? payload.exp : undefined,
            room: typeof payload.room === 'string' && payload.room ? payload.room : undefined
        };
    }
    catch {
        return undefined;
    }
}
/**
 * How long to wait before getting a new token, or undefined when the token
 * has no expiry to work from.
 *
 * @param {string} token - The room JWT the connection uses now.
 * @param {number} nowMs - The current time.
 * @returns {number|undefined}
 */
export function refreshDelayMs(token, nowMs) {
    const exp = roomTokenClaims(token)?.exp;
    if (exp === undefined) {
        return undefined;
    }
    return Math.max(MIN_REFRESH_DELAY_MS, (exp * 1000) - nowMs - REFRESH_LEAD_MS);
}
/**
 * The service URL with its token parameter changed to the new token. The
 * library (lib-jitsi-meet) adds `token=<jwt>` to the BOSH or WebSocket URL
 * when it creates the connection (modules/xmpp/xmpp.js, createConnection). A URL
 * without a token parameter comes back unchanged.
 *
 * @param {string} serviceUrl - The connection's service URL.
 * @param {string} token - The new room JWT.
 * @returns {string}
 */
export function withServiceToken(serviceUrl, token) {
    return serviceUrl.replace(/([?&]token=)[^&#]*/, (_match, prefix) => `${prefix}${token}`);
}
/**
 * Puts a new token into an open lib-jitsi-meet connection: the Strophe service
 * URL (what a resume connects to) and the token fields. The library has no
 * API for the URL (JitsiConnection.setToken changes only its own field).
 *
 * @param {Object} connection - The JitsiConnection.
 * @param {string} token - The new room JWT.
 * @returns {boolean} Whether the service URL now carries the new token.
 */
export function applyRoomToken(connection, token) {
    const strophe = connection?.xmpp?.connection?._stropheConn;
    if (!connection || !strophe || typeof strophe.service !== 'string' || !/[?&]token=/.test(strophe.service)) {
        return false;
    }
    strophe.service = withServiceToken(strophe.service, token);
    if (typeof connection.setToken === 'function') {
        connection.setToken(token);
    }
    else {
        connection.token = token;
    }
    if (connection.xmpp) {
        connection.xmpp.token = token;
    }
    return true;
}
/**
 * Creates the refresher. One timer at a time: a new connection replaces the
 * old schedule, and stop() ends it.
 *
 * @param {Object} deps - Clock, timers, the open connection and the mint.
 * @returns {Object}
 */
export function createRoomTokenRefresher(deps) {
    let timer;
    let generation = 0;
    let scheduled;
    /**
     * Stops the refresh. Given a connection, stops only when it is the one
     * being refreshed: a late close of an old connection must not stop the
     * new one's.
     *
     * @param {Object} [connection] - The connection that closed.
     * @returns {void}
     */
    const stop = (connection) => {
        if (connection && scheduled && connection !== scheduled) {
            return;
        }
        generation += 1;
        scheduled = undefined;
        if (timer !== undefined) {
            deps.clearTimeout(timer);
            timer = undefined;
        }
    };
    const later = (ms, run) => {
        timer = deps.setTimeout(() => {
            timer = undefined;
            run();
        }, ms);
    };
    const refresh = async (connection, roomName, mine) => {
        const isCurrent = () => mine === generation && deps.currentConnection() === connection;
        if (!isCurrent()) {
            return;
        }
        let token;
        try {
            token = await deps.mint(roomName);
        }
        catch (error) {
            if (!isCurrent()) {
                return;
            }
            const { status } = (error ?? {});
            const exp = roomTokenClaims(connection.token || '')?.exp;
            if (typeof status === 'number' && FINAL_STATUSES.includes(status)) {
                deps.log('room_token_refresh_refused', { status });
                return;
            }
            if (exp !== undefined && deps.now() + RETRY_DELAY_MS < exp * 1000) {
                deps.log('room_token_refresh_retry', { status: typeof status === 'number' ? status : undefined });
                later(RETRY_DELAY_MS, () => {
                    refresh(connection, roomName, mine);
                });
            }
            else {
                deps.log('room_token_refresh_gave_up');
            }
            return;
        }
        if (!isCurrent()) {
            return;
        }
        const claims = roomTokenClaims(token);
        if (claims?.room !== roomName || !applyRoomToken(connection, token)) {
            deps.log('room_token_refresh_not_applied');
            return;
        }
        deps.log('room_token_refreshed', {
            expiresInSeconds: claims.exp ? claims.exp - Math.floor(deps.now() / 1000) : undefined
        });
        schedule(connection);
    };
    /**
     * Plans the next refresh for this connection from its current token.
     *
     * @param {Object} connection - The open JitsiConnection.
     * @param {string} [fallbackRoom] - The room, when the token has no room claim.
     * @returns {void}
     */
    function schedule(connection, fallbackRoom) {
        stop();
        const token = connection?.token;
        if (!connection || typeof token !== 'string' || !token) {
            return;
        }
        const roomName = roomTokenClaims(token)?.room || fallbackRoom;
        const delay = refreshDelayMs(token, deps.now());
        if (!roomName || delay === undefined) {
            return;
        }
        const mine = generation;
        scheduled = connection;
        later(delay, () => {
            refresh(connection, roomName, mine);
        });
    }
    return {
        schedule,
        stop
    };
}
